import { DecisionGraph } from "@imminently/decisively-core";
import React, { useMemo } from "react";
import { parseRawGraph } from "../../../components/RuleGraph_FullRelease/__parseRawGraph";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import type { RuleGraph } from "@imminently/decisively-core";
import GraphProvider from "@imminently/decisively-ui/GraphProvider";

// this is the equivalent to the createStore method of Redux
// https://redux.js.org/api/createstore

/**
 * @deprecated Use the useGraph hook instead
 */
// @ts-ignore
const GraphContext = React.createContext();

export const useGraph = () => {
  const context = React.useContext(GraphContext);
  if (context === undefined) {
    return null;
    // unreachable...
    throw new Error("useGraph must be used within a GraphContext");
  }
  // the release's rule graph: a DecisionGraph (or, for older callers, its JSON); read it with DecisionGraph.from
  return (context ?? null) as RuleGraph | null;
};

export const ParsedGraphContext = React.createContext<DecisionGraph>(null as unknown as DecisionGraph);


/** A graph context provider that requires a full release and provides a fully parsed graph */
export const ParsedGraphContextProvider = ({ children }) => {
  const release = useFullRelease();

  const graph = useMemo<DecisionGraph>(
    () => (release ? parseRawGraph(release.rule_graph) : new DecisionGraph()),
    [release?.rule_graph],
  );

  return (
    <ParsedGraphContext.Provider value={graph}>
      <GraphProvider graph={graph}>{children}</GraphProvider>
    </ParsedGraphContext.Provider>
  );
};

export const useParsedGraph = () => {
  const context = React.useContext(ParsedGraphContext);
  if (context === undefined) {
    throw new Error("useParsedGraph must be used within a ParsedGraphContext");
  }
  return context;
};

export const useGraphGoalNodesOrdered = (
  graph: DecisionGraph | undefined,
  currentGoal: string | undefined,
  showHidden: boolean | undefined,
) => {
  return React.useMemo(() => {
    const nodes: any[] = [];
    if (graph) {
      for (const n of graph.ids()) {
        if (graph.field(n, "fk") || graph.field(n, "identifier")) {
          continue;
        }

        if (!showHidden && graph.field(n, "hidden")) {
          continue;
        }

        const entity = graph.field(n, "entity");
        const cfg = {
          id: graph.field(n, "id"),
          name: graph.field(n, "description"),
          isGoal: graph.predecessors(n).length === 0,
          path: graph.field(n, "path"),
          entity,
          isGlobal: entity === "global",
        };
        nodes.push(cfg);
      }
    }
    const goalNodes = {
      goal: nodes.filter((n) => n.isGoal),
      intermediate: nodes.filter((n) => !n.isGoal),
    };
    const flatGoals = [...goalNodes.goal, ...goalNodes.intermediate];

    if (currentGoal) {
      // if currentGoal is set, let's make sure that it's the first in the list, as we'll want to make sure it's visible
      const idx = flatGoals.findIndex((n) => n.id === currentGoal);
      if (idx > -1) {
        const currentGoalObj = flatGoals[idx];
        flatGoals.splice(idx, 1);
        flatGoals.unshift(currentGoalObj);
      }
    }

    return flatGoals;
  }, [graph, currentGoal, showHidden]);
};

export default GraphContext;